import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowLeft, ShieldCheck, Clock, Copy, Upload, Smartphone, Landmark, CreditCard } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { money, dateTime } from "@/lib/format";
import { PageTitle, SandboxNotice, StatusBadge, EmptyState } from "@/components/brand";
import Stepper from "@/components/Stepper";

const ICON = { netbanking: Landmark, card: CreditCard };
export const MethodIcon = ({ code }) => {
  const I = ICON[code] || Smartphone;
  return <span className="grid h-10 w-12 place-items-center rounded-md bg-white/95 text-black"><I className="h-5 w-5" /></span>;
};

const QUICK = [100, 500, 1000, 5000];
const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };

const compress = (file) => new Promise((res, rej) => {
  const img = new Image();
  img.onload = () => {
    const s = Math.min(1, 1280 / Math.max(img.width, img.height));
    const c = document.createElement("canvas");
    c.width = img.width * s; c.height = img.height * s;
    c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
    res(c.toDataURL("image/jpeg", 0.75));
  };
  img.onerror = rej;
  img.src = URL.createObjectURL(file);
});

const AmountStep = ({ amount, setAmount, onNext }) => (
  <div className="card-cr mx-auto max-w-xl p-7">
    <h2 className="text-xl font-bold">How many coins?</h2><p className="mt-1 text-sm text-cr-ink/75">1 rupee = 1 coin. Minimum ₹100.</p>
    <div className="mt-6 flex items-center gap-3 rounded-lg border border-cr-line bg-[#0d0f0f] px-5 py-4 focus-within:border-cr-gold">
      <span className="font-display text-3xl">₹</span>
      <input data-testid="deposit-amount-input" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))} className="w-full bg-transparent font-display text-4xl outline-none" placeholder="0" />
    </div>
    <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
      {QUICK.map((q) => <button key={q} data-testid={`quick-amount-${q}`} onClick={() => setAmount(String(q))} className={`rounded-lg border py-3 font-semibold transition-colors ${Number(amount) === q ? "border-cr-gold bg-cr-gold text-black" : "border-cr-line bg-cr-elevated hover:border-cr-gold/50"}`}>{money(q, { whole: true })}</button>)}
    </div>
    <p className="mt-4 text-sm text-cr-gold">You will get <b data-testid="deposit-coins-preview">{Number(amount || 0).toLocaleString("en-IN")}</b> coins</p>
    <button data-testid="deposit-step1-continue" disabled={Number(amount) < 100} onClick={onNext} className="btn-gold mt-6 w-full py-3.5 normal-case">Continue<ArrowRight className="h-5 w-5" /></button>
  </div>
);

const PayStep = ({ amount, upi, onBack, onNext }) => {
  const link = `upi://pay?pa=${encodeURIComponent(upi.upi_id)}&pn=${encodeURIComponent(upi.payee_name)}&am=${amount}&cu=INR`;
  return (
    <div className="card-cr mx-auto max-w-2xl p-7">
      <h2 className="text-xl font-bold">Pay {money(amount, { whole: true })} via UPI</h2><p className="mt-1 text-sm text-cr-ink/75">Scan the QR or pay to the UPI ID below, then note the UTR / transaction ID.</p>
      <div className="mt-6 grid items-center gap-6 sm:grid-cols-[auto_1fr]">
        <div data-testid="upi-qr" className="mx-auto rounded-xl bg-white p-3"><QRCodeSVG value={link} size={180} /></div>
        <div className="space-y-3 text-sm">
          <div className="rounded-lg border border-cr-line p-3"><p className="text-xs text-cr-muted">UPI ID</p>
            <p className="flex items-center justify-between gap-2 font-mono text-base"><span data-testid="upi-id">{upi.upi_id}</span><button aria-label="Copy UPI ID" data-testid="copy-upi-id" onClick={() => copy(upi.upi_id)}><Copy className="h-4 w-4 text-cr-gold" /></button></p></div>
          <div className="rounded-lg border border-cr-line p-3"><p className="text-xs text-cr-muted">Payee name</p><p data-testid="upi-payee">{upi.payee_name}</p></div>
          <div className="rounded-lg border border-cr-line p-3"><p className="text-xs text-cr-muted">Amount</p><p className="font-display text-lg text-cr-gold">{money(amount, { whole: true })}</p></div>
          <a href={link} data-testid="open-upi-app" className="btn-outline w-full py-2.5 normal-case sm:hidden"><Smartphone className="h-4 w-4" />Open UPI app</a>
        </div>
      </div>
      <div className="mt-6 flex items-center justify-between gap-4">
        <button data-testid="deposit-step2-back" onClick={onBack} className="flex items-center gap-2 font-semibold text-cr-gold"><ArrowLeft className="h-5 w-5" />Back</button>
        <button data-testid="deposit-step2-continue" onClick={onNext} className="btn-gold w-1/2 py-3.5 normal-case">I have paid<ArrowRight className="h-5 w-5" /></button>
      </div>
    </div>
  );
};

const ProofStep = ({ amount, onBack, onDone }) => {
  const [utr, setUtr] = useState("");
  const [shot, setShot] = useState(null);
  const [busy, setBusy] = useState(false);
  const pickFile = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image");
    try { setShot(await compress(f)); } catch { toast.error("Could not read this image"); }
  };
  const submit = async () => {
    setBusy(true);
    try { const { data } = await api.post("/wallet/upi-deposits", { amount: Number(amount), utr, screenshot: shot }); onDone(data); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="card-cr mx-auto max-w-xl p-7">
      <h2 className="text-xl font-bold">Submit payment proof</h2><p className="mt-1 text-sm text-cr-ink/75">Your admin checks the payment and credits {Number(amount).toLocaleString("en-IN")} coins.</p>
      <label className="mt-6 block text-sm font-semibold" htmlFor="utr">UTR / Transaction ID</label>
      <input id="utr" data-testid="upi-utr-input" value={utr} onChange={(e) => setUtr(e.target.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())} placeholder="12-digit UTR number" className="input-cr mt-2" />
      <label htmlFor="shot" className="mt-5 block text-sm font-semibold">Payment screenshot</label>
      <label htmlFor="shot" data-testid="upi-screenshot-drop" className="mt-2 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-cr-line p-5 text-sm text-cr-muted hover:border-cr-gold/50">
        {shot ? <img data-testid="upi-screenshot-preview" src={shot} alt="Payment screenshot" className="max-h-56 rounded" /> : <><Upload className="h-6 w-6 text-cr-gold" />Tap to upload screenshot</>}
      </label>
      <input id="shot" type="file" accept="image/*" data-testid="upi-screenshot-input" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
      <div className="mt-6 flex items-center justify-between gap-4">
        <button data-testid="deposit-step3-back" onClick={onBack} className="flex items-center gap-2 font-semibold text-cr-gold"><ArrowLeft className="h-5 w-5" />Back</button>
        <button data-testid="upi-submit-btn" disabled={busy || utr.length < 6 || !shot} onClick={submit} className="btn-gold w-1/2 py-3.5 normal-case">{busy ? "Submitting…" : "Submit"}</button>
      </div>
    </div>
  );
};

const DoneStep = ({ tx, onRestart }) => (
  <div data-testid="deposit-result" className="card-cr mx-auto max-w-xl p-8 text-center">
    <Clock className="mx-auto h-16 w-16 text-cr-gold" />
    <h2 data-testid="deposit-result-title" className="mt-4 text-2xl font-bold">Waiting for approval</h2>
    <p className="mt-2 text-sm text-cr-ink/75">Your admin will verify UTR <b className="font-mono">{tx.utr}</b>. Coins are added as soon as it is approved.</p>
    <p className="mt-4 font-mono text-xs text-cr-muted" data-testid="result-tx-id">{tx.reference}</p>
    <Link to="/account/transactions" className="btn-gold mt-6 w-full py-3.5 normal-case">View history<ArrowRight className="h-5 w-5" /></Link>
    <button data-testid="result-new-deposit" onClick={onRestart} className="mt-3 block w-full text-xs text-cr-muted hover:text-cr-ink">Add more coins</button>
  </div>
);

const Recent = ({ rows }) => rows.length > 0 && (
  <div className="card-cr mx-auto mt-8 max-w-2xl overflow-x-auto">
    <p className="px-5 pt-4 font-semibold">Recent UPI requests</p>
    <table className="w-full min-w-[480px] text-sm"><tbody>{rows.map((t) => (
      <tr key={t.id} data-testid={`upi-request-${t.id}`} className="border-t border-cr-line">
        <td className="px-5 py-3 font-mono text-xs">{t.utr}</td><td className="px-5 py-3">{money(t.amount, { whole: true })}</td>
        <td className="px-5 py-3"><StatusBadge status={t.status} /></td><td className="px-5 py-3 text-cr-muted">{dateTime(t.created_at)}</td>
      </tr>))}</tbody></table>
  </div>
);

export default function Deposit() {
  const [step, setStep] = useState(0);
  const [amount, setAmount] = useState("500");
  const [upi, setUpi] = useState(undefined);
  const [tx, setTx] = useState(null);
  const [rows, setRows] = useState([]);
  const loadRows = () => api.get("/wallet/upi-deposits").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { api.get("/wallet/upi").then((r) => setUpi(r.data)).catch(() => setUpi(null)); loadRows(); }, []);

  return (
    <div data-testid="deposit-page">
      <PageTitle crumbs={["Wallet", "Add Coins"]} title="Add Coins" subtitle="Pay with UPI and get coins after your admin approves." right={<div className="flex items-center gap-2"><ShieldCheck className="h-7 w-7 text-cr-gold" /><p className="text-sm font-semibold">Verified by your admin</p></div>} />
      {upi === null ? <EmptyState testid="upi-not-set" title="UPI not available" text="Your admin has not added a UPI ID yet. Please contact your admin." /> : <>
        <Stepper steps={["Amount", "Pay via UPI", "Upload proof", "Approval"]} current={step} />
        {step === 0 && <AmountStep amount={amount} setAmount={setAmount} onNext={() => setStep(1)} />}
        {step === 1 && upi && <PayStep amount={Number(amount)} upi={upi} onBack={() => setStep(0)} onNext={() => setStep(2)} />}
        {step === 2 && <ProofStep amount={amount} onBack={() => setStep(1)} onDone={(t) => { setTx(t); setStep(3); loadRows(); }} />}
        {step === 3 && tx && <DoneStep tx={tx} onRestart={() => { setTx(null); setStep(0); }} />}
      </>}
      <Recent rows={rows} />
      <SandboxNotice className="mx-auto mt-6 max-w-2xl" />
    </div>
  );
}
